/**
 * ArgoOffersRow — the "Argo can help" row beneath the dock greeting.
 *
 * Renders the grounded proactive offers from `useProactiveOffers` as chips:
 * a research offer ("Want me to research Acme before your next touch?") and a
 * reminder offer ("Remind you Friday about the renewal?"). Every offer is
 * wired to a REAL mechanism by the hook, so accepting can never dead-end:
 *
 *   • accept → fires the offer's action (research run / governed follow-up
 *     proposal) and drops the returned confirmation line into the transcript
 *     via `onConfirm`.
 *   • dismiss → hides the offer for 7 days (persisted).
 *
 * A gated research offer (worker unconfigured) renders as an honest note with
 * no accept button — it says "research runs when the Claude worker is
 * connected" instead of pretending it can act. Renders NOTHING when there are
 * no offers — Argo stays quiet when it has nothing grounded to propose.
 */

import { useState } from 'react';
import { HelpingHand, Loader2, Search, BellRing, X, Info } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { useProactiveOffers } from '../lib/agent/useProactiveOffers';
import type { Offer } from '../lib/agent/offers';

export interface ArgoOffersRowProps {
  /** False for viewers — offers render read-only (no accept/dismiss). */
  canMutate: boolean;
  /** Drop the accept/dismiss confirmation line into the dock transcript. */
  onConfirm: (line: string) => void;
}

export function ArgoOffersRow({ canMutate, onConfirm }: ArgoOffersRowProps) {
  const { offers, accept, dismiss } = useProactiveOffers();
  const [busyId, setBusyId] = useState<string | null>(null);

  if (offers.length === 0) return null;

  const onAccept = async (offer: Offer) => {
    if (!canMutate || !offer.action || busyId) return;
    setBusyId(offer.id);
    try {
      const line = await accept(offer);
      onConfirm(line);
      dismiss(offer); // done — don't re-offer the same thing this week
    } catch {
      onConfirm(`Couldn't complete that just now — try again from the record.`);
    } finally {
      setBusyId(null);
    }
  };

  const onDismiss = (offer: Offer) => {
    if (!canMutate) return;
    dismiss(offer);
    onConfirm(`Okay — I'll hold that offer for a week.`);
  };

  return (
    <GlassPanel
      variant="subtle"
      animate={false}
      style={{ padding: '10px 12px' }}
      aria-label="Argo can help"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span
          className="glass-pill glass-pill--magenta"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
        >
          <HelpingHand size={11} />
          Argo can help
        </span>
        <span className="t-meta" style={{ color: C.textDim }}>
          grounded in your records — where can I help?
        </span>
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {offers.map((offer) => {
          const gated = offer.gated || !offer.action;
          const busy = busyId === offer.id;
          const Icon = offer.kind === 'research' ? Search : BellRing;
          return (
            <div
              key={offer.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                padding: '6px 8px',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <span
                  className="t-card-title"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                >
                  {gated ? <Info size={12} /> : <Icon size={12} />}
                  {offer.text}
                </span>
                <span
                  className="t-meta"
                  style={{ color: C.textDim, display: 'block', marginTop: 2 }}
                  title={offer.evidence}
                >
                  {offer.evidence}
                </span>
              </div>
              {canMutate && !gated && (
                <button
                  type="button"
                  className="glass-btn"
                  disabled={busy}
                  onClick={() => void onAccept(offer)}
                  title={
                    offer.kind === 'research'
                      ? 'Runs the enrichment/research read on this prospect and saves it to their record'
                      : 'Queues a governed follow-up reminder into the Dashboard approvals inbox'
                  }
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    border: '1px solid rgba(255,0,255,0.25)',
                    flex: '0 0 auto',
                  }}
                >
                  {busy ? (
                    <Loader2 size={12} style={{ animation: 'auth-spin 0.8s linear infinite' }} />
                  ) : (
                    <Icon size={12} />
                  )}
                  {offer.kind === 'research' ? 'Research' : 'Remind me'}
                </button>
              )}
              {canMutate && (
                <button
                  type="button"
                  className="glass-btn"
                  onClick={() => onDismiss(offer)}
                  aria-label="Dismiss offer for a week"
                  title="Hide this offer for 7 days"
                  style={{ padding: '6px 8px', flex: '0 0 auto' }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </GlassPanel>
  );
}
